'use strict';

import moment from 'moment';
import store from 'store';
import { getMealTypesForDate } from './Meals';

/**
 * Pure utility functions for meal type calculations
 * No side effects, fully testable
 */

export const STORE_KEY = 'meal-feed-explicit-meal';
export const DEFAULT_STORE_EXPIRATION_HOURS = 8; // 8 hours

/**
 * Calculate the current meal type based on time of day
 * @param {Array} mealTypes - Available meal types for current date
 * @param {moment} now - Current time (for testing)
 * @returns {string|null} Meal type name or null if none found
 */
export function calculateTimeBasedMealType(mealTypes, now = moment()) {
    // Calculate time-based meal type

    if (!mealTypes || mealTypes.length === 0) {
        return null;
    }

    const sinceMidnight = now.diff(moment(now).startOf('day'), 'minutes');
    const sorted = [...mealTypes].sort((a, b) => a.begin - b.begin);

    // 1. Check if currently in any meal window (prioritize later start time)
    const activeMeals = mealTypes
        .filter(m => sinceMidnight >= m.begin && sinceMidnight <= m.end)
        .sort((a, b) => b.begin - a.begin);

    if (activeMeals.length > 0) {
        return activeMeals[0].name;
    }

    // 2. Find next meal or return last meal if all ended
    const nextMeal = sorted.find(m => sinceMidnight < m.begin);
    if (nextMeal) {
        return nextMeal.name;
    }

    // 3. All meals ended - return last meal (remains active until end of day)
    // Use the actual last meal from the sorted array, not a hardcoded value
    const lastMeal = sorted[sorted.length - 1]?.name || null;
    return lastMeal;
}

/**
 * Get explicit meal type from store if valid and not expired
 * @param {Array} mealTypes - Available meal types to validate against
 * @returns {string|null} Stored meal type or null if invalid/expired
 */
export function getStoredMealType(mealTypes) {
    try {
        const stored = store.get(STORE_KEY);

        if (!stored) {
            return null;
        }

        // Handle both old format (string) and new format (object)
        let mealType;
        let isExpired = false;
        let isDifferentDay = false;

        if (typeof stored === 'string') {
            // Old format - treat as invalid (force reset to use new format)
            clearStoredMealType();
            return null;
        } else if (stored && typeof stored === 'object') {
            // New format with expiration and date validation
            mealType = stored.mealType;
            const now = moment();
            const nowTimestamp = now.valueOf();
            isExpired = stored.expireAt && nowTimestamp > stored.expireAt;
            
            // Check if the selection is from a different day
            const today = now.format('YYYY-MM-DD');
            if (stored.selectedDate) {
                isDifferentDay = stored.selectedDate !== today;
            }

            // Clear if expired OR from a different day
            if (isExpired) {
                clearStoredMealType();
                return null;
            }

            if (isDifferentDay) {
                clearStoredMealType();
                return null;
            }
        } else {
            clearStoredMealType();
            return null;
        }

        // Validate stored meal type still exists in current meal types
        const isValid = mealTypes?.some(mt => mt.name === mealType);

        if (!isValid) {
            clearStoredMealType();
            return null;
        }

        return mealType;
    } catch (error) {
        return null;
    }
}

/**
 * Set explicit meal type in store with expiration
 * @param {string} mealType - Meal type to store
 * @param {number} expirationHours - Hours until expiration (default: 1)
 */
export function setStoredMealType(mealType, expirationHours = DEFAULT_STORE_EXPIRATION_HOURS) {
    try {
        const now = moment();
        const expireAt = now.clone().add(expirationHours, 'hours').valueOf();

        // Store as an object with meal type, expiration timestamp and selected date
        // This ensures manual selection is only valid for the current day
        const storageObject = {
            mealType: mealType,
            expireAt: expireAt,
            selectedDate: now.format('YYYY-MM-DD')  // Store the date of selection
        };

        store.set(STORE_KEY, storageObject);

        // Verify it was stored
        const verified = store.get(STORE_KEY);
    } catch (error) {
    }
}

/**
 * Clear explicit meal type from store
 */
export function clearStoredMealType() {
    try {
        store.remove(STORE_KEY);
    } catch (error) {
        // Error clearing stored meal type
    }
}

/**
 * Get the current active meal type following the hierarchy:
 * 1. Explicit store selection (if valid)
 * 2. Time-based calculation
 * 3. Fallback to default
 * @param {Object} preferences - User preferences
 * @param {Array} plans - User plans
 * @param {string} fallback - Fallback meal type (default: 'Dinner')
 * @param {moment} now - Current time (for testing)
 * @returns {string} Current active meal type
 */
export function getCurrentActiveMealType(preferences, plans, fallback = null, now = moment()) {
    // Get current active meal type following hierarchy

    const mealTypes = getMealTypesForDate(now, plans, preferences?.meal_types);

    // 1. Check for explicit store selection first
    const storedMealType = getStoredMealType(mealTypes);

    if (storedMealType) {
        return storedMealType;
    }

    // 2. Calculate time-based meal type
    const timeBasedMealType = calculateTimeBasedMealType(mealTypes, now);

    if (timeBasedMealType) {
        return timeBasedMealType;
    }

    // 3. Use intelligent fallback - first available meal type or provided fallback
    const intelligentFallback = fallback || mealTypes?.[0]?.name || null;
    return intelligentFallback;
}

/**
 * Find the next meal type in the sequence
 * @param {string} currentMealType - Current meal type
 * @param {Array} mealTypes - Available meal types
 * @returns {string|null} Next meal type or null if at end
 */
export function getNextMealType(currentMealType, mealTypes) {
    if (!mealTypes || mealTypes.length === 0) return null;

    const sorted = [...mealTypes].sort((a, b) => a.begin - b.begin);
    const currentIndex = sorted.findIndex(mt => mt.name === currentMealType);

    if (currentIndex === -1 || currentIndex === sorted.length - 1) {
        return null; // Not found or at end
    }

    return sorted[currentIndex + 1].name;
}

/**
 * Find the previous meal type in the sequence
 * @param {string} currentMealType - Current meal type
 * @param {Array} mealTypes - Available meal types
 * @returns {string|null} Previous meal type or null if at beginning
 */
export function getPreviousMealType(currentMealType, mealTypes) {
    if (!mealTypes || mealTypes.length === 0) return null;

    const sorted = [...mealTypes].sort((a, b) => a.begin - b.begin);
    const currentIndex = sorted.findIndex(mt => mt.name === currentMealType);

    if (currentIndex === -1 || currentIndex === 0) {
        return null; // Not found or at beginning
    }

    return sorted[currentIndex - 1].name;
}

/**
 * Check if a meal type exists in the given meal types array
 * @param {string} mealType - Meal type to check
 * @param {Array} mealTypes - Available meal types
 * @returns {boolean} True if meal type exists
 */
export function isValidMealType(mealType, mealTypes) {
    return mealTypes?.some(mt => mt.name === mealType) || false;
}

/**
 * Get fallback meal type when current selection is invalid
 * @param {Array} mealTypes - Available meal types
 * @param {moment} now - Current time (for testing)
 * @returns {string} Fallback meal type
 */
export function getFallbackMealType(mealTypes, now = moment()) {
    if (!mealTypes || mealTypes.length === 0) {
        return 'Dinner';
    }

    // Try time-based calculation first
    const timeBasedMealType = calculateTimeBasedMealType(mealTypes, now);
    if (timeBasedMealType) {
        return timeBasedMealType;
    }

    // Fallback to first meal type or 'Dinner'
    return mealTypes[0]?.name || 'Dinner';
}

/**
 * Debug utility to log meal type calculations (internal users only)
 * @param {string} message - Debug message
 * @param {Object} data - Additional data to log
 */
export function debugLog(message, data = {}) {
    // Only log for internal users - check if we have user context
    if (typeof window !== 'undefined' && window.UserStore) {
        const user = window.UserStore.getUser?.();
        if (user?.is_internal) {
        }
    }
}